import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface CustomStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus?: {
    status?: string;
    message?: string;
    emoji?: string;
  } | null;
  onSave?: (status: { status: string; message: string; emoji: string }) => void;
}

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online', color: 'bg-green-500' },
  { value: 'away', label: 'Ausente', color: 'bg-yellow-500' },
  { value: 'busy', label: 'Ocupado', color: 'bg-red-500' },
  { value: 'offline', label: 'Offline', color: 'bg-gray-500' },
];

const EMOJI_OPTIONS = ['😊', '🎯', '💼', '🚀', '☕', '🔥', '💡', '🎉'];

export function CustomStatusModal({
  isOpen,
  onClose,
  currentStatus,
  onSave,
}: CustomStatusModalProps) {
  const [status, setStatus] = useState(currentStatus?.status || 'online');
  const [message, setMessage] = useState(currentStatus?.message || '');
  const [emoji, setEmoji] = useState(currentStatus?.emoji || '😊');

  useEffect(() => {
    if (isOpen && currentStatus) {
      setMessage(currentStatus.message || '');
      setEmoji(currentStatus.emoji || '😊');
    }
  }, [isOpen, currentStatus]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave?.({ status, message, emoji });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-[#0a0a0a] border border-white/10 rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Editar Status</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-slate-400 mb-2">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatus(opt.value)}
                className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-left ${
                  status === opt.value
                    ? 'border-red-500 bg-red-500/10 text-white'
                    : 'border-white/10 text-slate-300 hover:border-white/20'
                }`}
              >
                <span className={`w-3 h-3 rounded-full shrink-0 ${opt.color}`} />
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm text-slate-400 mb-2">Emoji</label>
          <div className="flex gap-2 flex-wrap">
            {EMOJI_OPTIONS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEmoji(e)}
                className={`text-2xl p-2 rounded-lg border transition-all ${
                  emoji === e
                    ? 'border-red-500 bg-red-500/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm text-slate-400 mb-2">Mensagem</label>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ex: Em reunião até 15h"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
            maxLength={50}
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
